import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLanguage, type Language } from "@/contexts/LanguageContext";
import { isProductionDomain } from "@/lib/domain";
import { buildLocalizedPath, stripLangPrefix } from "@/lib/i18n";

const SITE_ORIGIN = "https://www.fluxabee.com";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;

const OG_LOCALE: Record<Language, string> = {
  en: "en_US",
  pl: "pl_PL",
  de: "de_DE",
};

const ALL_LANGS: Language[] = ["en", "pl", "de"];

type JsonLd = Record<string, unknown>;

interface SEOProps {
  titleKey: string;
  descriptionKey: string;
  /**
   * Optional override for the logical (language-agnostic) path. When omitted,
   * the current pathname is read via `useLocation()` and stripped of any
   * `/pl` or `/de` prefix. Pass a value only for pages that need a forced
   * canonical (rare).
   */
  path?: string;
  jsonLd?: JsonLd | JsonLd[];
  /** Force noindex even on production domain (e.g. utility pages). */
  noindex?: boolean;
  /** Override default OG image absolute URL. */
  image?: string;
  /**
   * Explicit per-language URL paths (with leading slash, no origin) for
   * pages whose slug differs across languages and therefore can't be
   * derived by re-prefixing a single logical path. Used by the long-form
   * guide articles (`/guide/...`, `/pl/poradnik/...`, `/de/ratgeber/...`).
   * When provided, this drives canonical + hreflang + og:url instead of
   * the default logical-path machinery.
   */
  hreflangAlternates?: Record<Language, string>;
}

/**
 * Default robots meta for all pages. Mounted high in the tree (in App.tsx),
 * it sets `noindex, nofollow` for every route. Pages that want to be
 * indexable render their own <SEO> deeper in the tree - react-helmet-async
 * dedupes <meta name="robots"> by the `name` attribute and the
 * last-mounted instance wins, so the page-level <SEO> overrides this.
 */
export const DefaultNoIndex = () => (
  <Helmet>
    <meta name="robots" content="noindex, nofollow" />
  </Helmet>
);

export const SEO = ({
  titleKey,
  descriptionKey,
  path,
  jsonLd,
  noindex = false,
  image = DEFAULT_OG_IMAGE,
  hreflangAlternates,
}: SEOProps) => {
  const { t, language } = useLanguage();
  const location = useLocation();

  // Logical path without language prefix (used to build hreflang alternates).
  // If caller passed `path`, assume it's already the canonical logical path.
  // Otherwise derive from current URL.
  const logicalPath = path
    ? stripLangPrefix(path)
    : stripLangPrefix(location.pathname);

  const title = t(titleKey);
  const description = t(descriptionKey);

  // Canonical = current-language URL. When the page provides an explicit
  // hreflang map (guides), use it directly; otherwise derive from logical
  // path via `buildLocalizedPath`.
  const canonicalPath = hreflangAlternates
    ? hreflangAlternates[language]
    : buildLocalizedPath(logicalPath, language);
  const canonical = `${SITE_ORIGIN}${canonicalPath}`;

  const shouldNoIndex = noindex || !isProductionDomain;
  const robots = shouldNoIndex ? "noindex, nofollow" : "index, follow";
  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  // hreflang alternates: one per supported language + x-default (EN).
  const alternates = ALL_LANGS.map((lang) => ({
    hreflang: lang,
    href: `${SITE_ORIGIN}${
      hreflangAlternates
        ? hreflangAlternates[lang]
        : buildLocalizedPath(logicalPath, lang)
    }`,
  }));
  const xDefaultHref = `${SITE_ORIGIN}${
    hreflangAlternates
      ? hreflangAlternates.en
      : buildLocalizedPath(logicalPath, "en")
  }`;

  // og:locale:alternate - the 2 languages other than the current one.
  const altLocales = ALL_LANGS.filter((l) => l !== language).map(
    (l) => OG_LOCALE[l]
  );

  return (
    <Helmet htmlAttributes={{ lang: language }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />

      {/* hreflang alternates */}
      {alternates.map((alt) => (
        <link
          key={alt.hreflang}
          rel="alternate"
          hrefLang={alt.hreflang}
          href={alt.href}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={xDefaultHref} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={OG_LOCALE[language]} />
      {altLocales.map((loc) => (
        <meta key={loc} property="og:locale:alternate" content={loc} />
      ))}
      <meta property="og:site_name" content="Fluxabee" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {jsonLdArray.map((entry, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
